import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { convertFromNative } from '../components/VehicleEditor/Units';
import { isEncryptionEnabled, getStoredPassphrase, decryptData } from '../utils/encryption';
import * as XLSX from 'xlsx';
import '../styles.css';

function SetupHistory({ subscriptionStatus }) {
  const { vehicleId } = useParams();
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState({});
  const [vehicle, setVehicle] = useState(null);
  const [params, setParams] = useState([]);
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [filterText, setFilterText] = useState('');
  const [hideUnchanged, setHideUnchanged] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const abbreviateParamName = (name) => {
    return name
      .replace(/Front Left/g, 'FL')
      .replace(/Front Right/g, 'FR')
      .replace(/Rear Left/g, 'RL')
      .replace(/Rear Right/g, 'RR');
  };

  const loadData = useCallback(async () => {
    setSyncStatus('syncing');
    try {
      // Load vehicle
      const vehicleDoc = await getDoc(doc(db, `users/${user.uid}/vehicles/${vehicleId}`));
      if (!vehicleDoc.exists()) {
        alert('Vehicle not found');
        navigate('/');
        return;
      }
      const vehicleData = vehicleDoc.data();
      setVehicle(vehicleData);

      // Load sessions
      const sessionsQuery = query(
        collection(db, `users/${user.uid}/sessions`),
        where('vehicleId', '==', Number(vehicleId)),
        orderBy('time', 'asc')
      );
      const sessionsSnap = await getDocs(sessionsQuery);
      let sessionsData = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Decrypt encrypted sessions if needed
      const encryptionEnabled = await isEncryptionEnabled(user, db);
      if (encryptionEnabled) {
        const passphrase = getStoredPassphrase();
        if (passphrase) {
          sessionsData = sessionsData.map(session => {
            if (session.encrypted) {
              try {
                const decryptedData = decryptData(session.data, passphrase);
                // Handle both old and new formats
                if (decryptedData.vehicleId && decryptedData.eventId) {
                  // Old format: entire session was encrypted
                  return {
                    id: session.id,
                    ...decryptedData
                  };
                } else {
                  // New format: merge with plain metadata
                  return {
                    id: session.id,
                    ...session,
                    ...decryptedData
                  };
                }
              } catch (error) {
                console.error(`Failed to decrypt session ${session.id}:`, error);
                return session; // Return encrypted session as-is if decryption fails
              }
            }
            return session;
          });
        }
      }

      setSessions(sessionsData);

      // Load events
      const eventIds = [...new Set(sessionsData.map(s => s.eventId))];
      const eventsMap = {};
      for (const eventId of eventIds) {
        const eventDoc = await getDoc(doc(db, `users/${user.uid}/events/${eventId}`));
        if (eventDoc.exists()) {
          eventsMap[eventId] = eventDoc.data();
        }
      }
      setEvents(eventsMap);

      // Build params
      const allParams = [];
      const categories = ['Suspension', 'Tires', 'Brakes', 'Aero', 'Drivetrain', 'Engine', 'Chassis', 'Electronics'];
      categories.forEach(cat => {
        const tab = vehicleData.tabs.find(t => t.name === cat);
        if (tab) {
          tab.groups.forEach(group => {
            group.params.forEach(param => {
              if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
                const positions = param.isFrontRearGroup ? ['Front', 'Rear'] : param.leftRight ? ['Left', 'Right'] : ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
                positions.forEach(pos => {
                  allParams.push({
                    name: `${param.name} (${pos})`,
                    unit: param.unit,
                    category: cat
                  });
                });
              } else {
                allParams.push({
                  name: param.name,
                  unit: param.unit,
                  category: cat
                });
              }
            });
          });
        }
      });
      setParams(allParams);

      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    } catch (err) {
      console.error('Load data error:', err);
      setSyncStatus(`Error: ${err.message}`);
    }
  }, [user, vehicleId, navigate]);

  useEffect(() => {
    if (loading) return;
    if (error || !user) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, loading, error, vehicleId, navigate, loadData]);

  const getFilteredParams = () => {
    let filtered = params;
    if (hideUnchanged) {
      filtered = filtered.filter(param => {
        const values = sessions.map(s => s.setup?.[param.name] || '');
        return new Set(values).size > 1;
      });
    }
    if (filterText) {
      filtered = filtered.filter(param =>
        param.name.toLowerCase().includes(filterText.toLowerCase()) ||
        param.category.toLowerCase().includes(filterText.toLowerCase())
      );
    }
    return filtered;
  };

  const getFilteredSessions = () => sessions;

  const formatDate = (time) => {
    const date = new Date(time);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (time) => {
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatValue = (value, unit) => {
    if (!value && value !== 0) return '';
    if (unit === 'text') return value;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    // For display, use 1-2 decimal places based on unit type
    if (unit === '%' || unit === 'rpm' || unit === 'V' || unit === 'A' || unit === 'W' || unit === 'kWh') {
      return numValue.toFixed(0);
    } else if (unit.includes('/') || unit.includes('°') || unit === 'text' || unit === 'rpm') {
      return numValue.toFixed(1);
    } else {
      return numValue.toFixed(2);
    }
  };

  const exportToExcel = () => {
    const filteredParams = getFilteredParams();
    const filteredSessions = getFilteredSessions();

    const data = [];

    // Row 1: Parameter Name
    const row1 = ['', '', ''];
    filteredParams.forEach(param => row1.push(param.name));
    data.push(row1);

    // Row 2: Unit
    const row2 = ['', '', ''];
    filteredParams.forEach(param => row2.push(param.unit || ''));
    data.push(row2);

    // Rows 3-4: Blank
    data.push(['']);
    data.push(['']);

    // Data rows
    filteredSessions.forEach(session => {
      const event = events[session.eventId];
      const row = [
        `${event?.name || 'Unknown Event'} ${session.sessionName || 'Unnamed Session'}`,
        formatDate(session.time),
        formatTime(session.time)
      ];
      filteredParams.forEach(param => {
        row.push(session.setup?.[param.name] ? formatValue(convertFromNative(session.setup?.[param.name], param.unit), param.unit) : '');
      });
      data.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Setup History');
    const fileName = `${(vehicle?.name || 'Vehicle').replace(/[^a-zA-Z0-9]/g, '_')}_hot_setup_history.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleExportClick = () => {
    if (subscriptionStatus?.plan === 'enthusiast') {
      setShowExportDialog(true);
    } else {
      exportToExcel();
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const filteredParams = getFilteredParams();
  const filteredSessions = getFilteredSessions();

  return (
    <div className="setup-history-container">
      <Header syncStatus={syncStatus} />
      <div className="page-title">
        <h1>Setup History - {vehicle?.name || 'Vehicle'}</h1>
        <button onClick={() => navigate(-1)} className="close-button">✕</button>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by text..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          className="filter-input"
        />
        <label>
          <input
            type="checkbox"
            checked={hideUnchanged}
            onChange={e => setHideUnchanged(e.target.checked)}
          />
          Hide unchanged parameters
        </label>
        <button onClick={handleExportClick} className={`btn btn-primary ${subscriptionStatus?.plan === 'enthusiast' ? 'disabled' : ''}`}>
          Export to Excel
          {subscriptionStatus?.plan === 'enthusiast' && <span className="pro-btn-overlay">PRO</span>}
        </button>
      </div>
      <div className="table-container">
        {syncStatus === 'syncing' ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '50px',
            gap: '12px'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '3px solid rgba(59, 130, 246, 0.3)',
              borderTop: '3px solid #3B82F6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Loading setup history...
          </div>
        ) : (
          <table className="setup-history-table">
          <thead>
            <tr>
              <th rowSpan="2">Session</th>
              {Object.entries(
                filteredParams.reduce((acc, param) => {
                  if (!acc[param.category]) acc[param.category] = [];
                  acc[param.category].push(param);
                  return acc;
                }, {})
              ).map(([category, params]) => (
                <th key={category} colSpan={params.length}>{category}</th>
              ))}
            </tr>
            <tr>
              {filteredParams.map((param, idx) => (
                <th key={idx} title={`${param.category}: ${param.name}`}>
                  {abbreviateParamName(param.name)}
                  {param.unit && (
                    <>
                      <br />(<small>{param.unit}</small>)
                    </>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSessions.map(session => {
              const event = events[session.eventId];
              return (
                <tr key={session.id}>
                  <td>
                    <div>{event?.name || 'Unknown Event'}</div>
                    <div>{session.sessionName || 'Unnamed Session'}</div>
                    <div>{formatDate(session.time)} {formatTime(session.time)}</div>
                  </td>
                  {filteredParams.map((param, idx) => (
                    <td key={idx}>{session.setup?.[param.name] ? formatValue(convertFromNative(session.setup?.[param.name], param.unit), param.unit) : ''}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
      {showExportDialog && (
        <ConfirmationDialog
          title="Pro Feature"
          message="Excel Exports are available with the Pro plan"
          onConfirm={handleUpgrade}
          onCancel={() => setShowExportDialog(false)}
          confirmText="Upgrade to Pro"
          cancelText="Ok"
        />
      )}
    </div>
  );
}

export default SetupHistory;